import Logo from "@/components/Logo";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Linkedin, Instagram, X as XIcon } from "lucide-react";
import { SOFT_LAUNCH, WAITLIST_URL } from "@/config/softLaunch";

export default function Footer() {
  const navigate = useNavigate();
  const location = useLocation();

  // Temporary GTM soft-launch (see src/config/softLaunch.js) — scrolls to the
  // landing page's Featured Routes section instead of navigating to /explore.
  const scrollToFeatured = (e) => {
    e.preventDefault();
    if (location.pathname === "/") {
      document.getElementById("featured-section")?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/#featured-section");
    }
  };

  return (
    <footer
      data-testid="site-footer"
      className="bg-[var(--ink)] text-[#E8E0D6] mt-24"
    >
      <div className="max-w-[1280px] mx-auto px-6 sm:px-10 py-16 grid gap-10 md:grid-cols-[1.4fr_1fr_1fr_1fr]">
        <div>
          <Logo inverse size={32} />
          <p className="mt-5 text-[14px] text-[#bdb1a3] max-w-[320px] leading-relaxed">
            A marketplace for hyper-specific travel itineraries. By people who've actually been there.
          </p>
          <div className="mt-6 mono text-[10px] tracking-[0.18em] uppercase text-[#8A7E76]">
            ◆ Vol. I — 2026
          </div>
          <div className="mt-6">
            <div className="mono text-[10px] tracking-[0.18em] uppercase text-[#8A7E76] mb-3">
              Follow the journey
            </div>
            <div className="flex items-center gap-4">
              <a
                href="https://www.linkedin.com/company/nirno/home/?viewAsMember=true"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Nirno on LinkedIn"
                data-testid="footer-social-linkedin"
                className="text-[#8A7E76] hover:text-[var(--red)] transition-colors"
              >
                <Linkedin size={18} strokeWidth={1.75} />
              </a>
              <a
                href="https://x.com/thenirno"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Nirno on X"
                data-testid="footer-social-x"
                className="text-[#8A7E76] hover:text-[var(--red)] transition-colors"
              >
                <XIcon size={18} strokeWidth={1.75} />
              </a>
              <a
                href="https://www.instagram.com/thenirno/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Nirno on Instagram"
                data-testid="footer-social-instagram"
                className="text-[#8A7E76] hover:text-[var(--red)] transition-colors"
              >
                <Instagram size={18} strokeWidth={1.75} />
              </a>
            </div>
          </div>
        </div>
        <FootCol title="Travel">
          {SOFT_LAUNCH ? (
            <a href="/#featured-section" onClick={scrollToFeatured} data-testid="footer-explore" className="block py-1 text-[13px] text-[#E8E0D6] hover:text-[var(--red)] transition-colors">Explore routes</a>
          ) : (
            <FootLink to="/explore">Explore routes</FootLink>
          )}
          {SOFT_LAUNCH ? (
            <a href="/#featured-section" onClick={scrollToFeatured} data-testid="footer-free" className="block py-1 text-[13px] text-[#E8E0D6] hover:text-[var(--red)] transition-colors">Free itineraries</a>
          ) : (
            <FootLink to="/explore?free=1">Free itineraries</FootLink>
          )}
          {SOFT_LAUNCH ? (
            <a href={WAITLIST_URL} target="_blank" rel="noopener noreferrer" data-testid="footer-join" className="block py-1 text-[13px] text-[#E8E0D6] hover:text-[var(--red)] transition-colors">Join Nirno</a>
          ) : (
            <FootLink to="/auth?mode=signup">Join Nirno</FootLink>
          )}
        </FootCol>
        <FootCol title="Company">
          <FootLink to="/terms">Terms &amp; Conditions</FootLink>
          <FootLink to="/privacy">Privacy Policy</FootLink>
          <FootLink to="/refunds">Cancellation &amp; Refund Policy</FootLink>
          <FootLink to="/contact">Contact for Support</FootLink>
        </FootCol>
        <FootCol title="Connect">
          <FootLink href="mailto:hello@nirno.co.in">hello@nirno.co.in</FootLink>
          <a
            href="mailto:hello@nirno.co.in?subject=Interested in being a creator on Nirno"
            data-testid="footer-sell-link"
            className="block py-1 text-[13px] text-[#FBF7F2] hover:text-[var(--red)] transition-colors"
          >
            Interested in being a creator? Reach out →
          </a>
        </FootCol>
      </div>
      <div className="border-t border-[#2a201b] py-6 text-center mono text-[10px] tracking-[0.18em] uppercase text-[#8A7E76]">
        © 2026 Nirno · Real trips, by real travellers
      </div>
    </footer>
  );
}

const FootCol = ({ title, children }) => (
  <div>
    <div className="mono text-[10px] tracking-[0.18em] uppercase text-[#8A7E76] mb-4">◆ {title}</div>
    <div className="flex flex-col gap-1.5">{children}</div>
  </div>
);

const FootLink = ({ children, to, href }) => {
  const cls = "block py-1 text-[13px] text-[#E8E0D6] hover:text-[var(--red)] transition-colors";
  if (to) return <Link to={to} className={cls}>{children}</Link>;
  return <a href={href} className={cls}>{children}</a>;
};
